import React, { useMemo } from 'react';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { StockBadge, calculateStockStatus } from '@components/atoms/StockBadge';
import { cn } from '@utils/cn';
import { Warehouse } from '@models/warehouse.model';
import { CatalogAvailability } from '@utils/stockAvailability';
import WarehouseIcon from '@mui/icons-material/Warehouse';

export interface WarehouseOption extends SelectOption {
  warehouse: Warehouse;
  /** Available quantity of the material passed via `materialAvailability`. */
  quantityAvailable?: number;
}

interface WarehouseSelectorProps {
  warehouses: Warehouse[];
  value?: WarehouseOption | null;
  onChange: (option: WarehouseOption | null) => void;
  /**
   * Per-warehouse availability for a specific material. When provided, options
   * are sorted richest-first and annotated with how much is actually there, so
   * the user can see which warehouse to pull from instead of guessing.
   */
  materialAvailability?: CatalogAvailability;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  showInactive?: boolean;
  className?: string;
}

export const WarehouseSelector: React.FC<WarehouseSelectorProps> = ({
  warehouses,
  value,
  onChange,
  materialAvailability,
  placeholder = 'Select warehouse...',
  isDisabled = false,
  isLoading = false,
  error = false,
  helperText,
  label,
  required = false,
  showInactive = false,
  className,
}) => {
  // Build options
  const options = useMemo(() => {
    const filtered = showInactive
      ? warehouses
      : warehouses.filter((w) => w.isActive);

    const availabilityByWarehouse = new Map(
      (materialAvailability?.byWarehouse ?? []).map((w) => [
        w.warehouseId,
        w.quantityAvailable,
      ]),
    );

    const mapped = filtered.map((warehouse) => ({
      value: warehouse.warehouseId,
      label: warehouse.name,
      warehouse,
      quantityAvailable: materialAvailability
        ? availabilityByWarehouse.get(warehouse.warehouseId) ?? 0
        : undefined,
      isDisabled: !warehouse.isActive,
    })) as WarehouseOption[];

    if (!materialAvailability) return mapped;

    // Richest warehouse first — that's the one the user almost always wants.
    return mapped.sort(
      (a, b) => (b.quantityAvailable ?? 0) - (a.quantityAvailable ?? 0),
    );
  }, [warehouses, showInactive, materialAvailability]);

  // Custom option renderer - context differentiates menu vs selected value
  const formatOptionLabel = (
    option: WarehouseOption,
    { context }: { context: 'menu' | 'value' },
  ) => {
    const showStock = option.quantityAvailable !== undefined;

    // Compact rendering for the selected value in the control
    if (context === 'value') {
      return (
        <div className="flex items-center gap-2">
          <WarehouseIcon className="text-indigo-500" sx={{ fontSize: 16 }} />
          <span className="truncate">{option.warehouse.name}</span>
          {showStock && (
            <StockBadge
              status={calculateStockStatus(option.quantityAvailable as number)}
              size="sm"
            />
          )}
          {option.warehouse.isDefault && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded">
              Default
            </span>
          )}
        </div>
      );
    }

    // Rich rendering for dropdown menu
    return (
      <div className="flex items-center gap-3 w-full py-0.5">
        <div className="w-8 h-8 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
          <WarehouseIcon className="text-indigo-500" sx={{ fontSize: 16 }} />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-sm font-medium truncate',
                option.isDisabled && 'text-gray-400 dark:text-gray-600',
              )}>
              {option.warehouse.name}
            </span>
            {option.warehouse.isDefault && (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded">
                Default
              </span>
            )}
            {!option.warehouse.isActive && (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                Inactive
              </span>
            )}
          </div>
          {option.warehouse.address && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {option.warehouse.address}
            </span>
          )}
        </div>
        {showStock && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {option.quantityAvailable} available
            </span>
            <StockBadge
              status={calculateStockStatus(option.quantityAvailable as number)}
              size="sm"
            />
          </div>
        )}
      </div>
    );
  };

  const handleChange = (selected: any) => {
    if (!selected) {
      onChange(null);
      return;
    }
    const option = options.find((opt) => opt.value === selected.value);
    onChange(option || null);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <ReactSelect
        options={options}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoading}
        error={error}
        helperText={helperText}
        isSearchable
        isClearable
        formatOptionLabel={formatOptionLabel as any}
      />
    </div>
  );
};

export default WarehouseSelector;
